import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Zap, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}


const PACK_META = {
  pack_a: { badge: 'Starter',    icon: Zap,      color: 'from-sky-500 to-sky-400' },
  pack_b: { badge: 'Popular',    icon: Sparkles, color: 'from-amber-500 to-amber-400', highlight: true },
  pack_c: { badge: 'Best value', icon: Zap,      color: 'from-emerald-500 to-emerald-400' },
};


/**
 * BuyCreditsModal — 3-pack top-up UI wired through the SuperAdmin's
 * Razorpay integration. Balance NEVER updates from this modal; that's
 * strictly the webhook's job. We just fire Razorpay Checkout, then
 * poll `/api/wallet` once on `payment.success` to reflect the new
 * balance if the webhook has landed by then.
 */
export function BuyCreditsModal({ open, onOpenChange, packs, user, onCreditsAdded }) {
  const [selected, setSelected] = useState('pack_b');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setSelected('pack_b'); }, [open]);

  const packEntries = Object.entries(packs || {});

  const startCheckout = async () => {
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay SDK failed to load'); return; }

      const orderRes = await axios.post(`${API}/wallet/topup-order`, { pack: selected });
      const {
        razorpay_order_id, razorpay_key_id, amount_paise, credits,
      } = orderRes.data;

      const rzp = new window.Razorpay({
        key: razorpay_key_id,
        amount: amount_paise,
        currency: 'INR',
        order_id: razorpay_order_id,
        name: 'Rupiwise Credit Wallet',
        description: `${credits} credits top-up`,
        prefill: {
          email: user?.email,
          name: user?.company_name || user?.name,
          contact: user?.phone,
        },
        notes: {
          purpose: 'wallet_topup',
          organization_id: user?.organization_id,
          credits: String(credits),
        },
        theme: { color: '#f59e0b' },
        handler: async () => {
          // Balance is credited by the webhook; give it a couple of
          // seconds to land before refetching. The webhook usually
          // beats the browser callback by hundreds of ms so a small
          // retry loop is enough for the UI to reflect the new number.
          toast.success('Payment received — updating balance…');
          for (let i = 0; i < 4; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 1500));
            try {
              // eslint-disable-next-line no-await-in-loop
              const w = await axios.get(`${API}/wallet`);
              if (w.data?.wallet) {
                onCreditsAdded?.(w.data.wallet);
                if (i > 0) break;
              }
            } catch { /* keep polling */ }
          }
          onOpenChange(false);
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed');
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create order');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="buy-credits-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-theme-primary-strong">
            <Zap className="w-5 h-5 text-theme-primary" /> Buy Rupiwise Credits
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">
            1 credit = ₹1 (GST-inclusive). Credits burn only on cloud-sync flows
            (IRN, EWB). Offline JSON/Excel exports stay free.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-2" data-testid="pack-grid">
          {packEntries.map(([key, pack]) => {
            const meta = PACK_META[key] || PACK_META.pack_a;
            const Icon = meta.icon;
            const isSel = selected === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelected(key)}
                data-testid={`pack-${key}`}
                className={`text-left rounded-xl border p-4 transition-all relative overflow-hidden bg-white dark:bg-slate-900/60
                  ${isSel
                    ? 'border-theme-primary ring-2 shadow-md'
                    : 'border-slate-200 dark:border-slate-700 hover:border-theme-primary-soft'}`}
                style={isSel ? { boxShadow: '0 0 0 2px var(--theme-primary-light)' } : undefined}
              >
                {meta.highlight && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-500 dark:text-slate-950 dark:border-transparent px-1.5 py-0.5 rounded">
                    {meta.badge}
                  </span>
                )}
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">{meta.badge}</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">₹{(pack.amount_paise / 100).toLocaleString('en-IN')}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{pack.credits.toLocaleString('en-IN')} credits</div>
                <div className="text-[10px] text-slate-500 mt-2">Enough for {pack.credits} IRN/EWB calls</div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={startCheckout}
            disabled={busy || !selected}
            data-testid="buy-credits-checkout"
            className="bg-theme-primary hover:bg-theme-primary font-semibold"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening Razorpay…</>
            ) : (
              <>Pay with Razorpay</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default BuyCreditsModal;
